import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { CardHeaderComponent } from 'common/card/header/card-header.component';
import { TranslateModule } from '@ngx-translate/core';
import { ApiV1Service, ManufacturerProperties } from 'api';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { email, form, FormField, FormRoot, maxLength, pattern } from '@angular/forms/signals';
import { Router } from '@angular/router';
import { AppService } from 'app/app.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { requiredAndNotWhitespaceOnlySignal } from 'common/form/form.service';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';
import { IconDirective } from 'common/icon/icon.directive';

type EditModel = {
  brandName: string;
  legalEntityName: string;
  productContact: string;
  productContactLocation: string;
  productContactEmail: string;
  productContactPhone: string;
};

@Component({
  selector: 'comp-manufacturer-details',
  templateUrl: './manufacturer-details.component.html',
  imports: [
    CardHeaderComponent,
    TranslateModule,
    FormsModule,
    MatInput,
    FormField,
    FormRoot,
    MatFormField,
    MatLabel,
    MatProgressSpinner,
    MatError,
    AutofocusDirective,
    ButtonDirective,
    IconDirective,
  ],
  styleUrls: ['./manufacturer-details.component.scss'],
})
export class ManufacturerDetailsComponent {
  private readonly MAX_LENGTH = 255;
  private readonly PHONE_NUMBER_PATTERN = /^$|^\+?[0-9][0-9\s().-]{5,}$/;

  private readonly apiService = inject(ApiV1Service);
  private readonly appService = inject(AppService);
  private readonly router = inject(Router);

  private readonly brandNameInput = viewChild<ElementRef<HTMLInputElement>>('brandNameInput');

  readonly manufacturer = signal<ManufacturerProperties | null>(null);

  readonly editModel = signal<EditModel>({
    brandName: '',
    legalEntityName: '',
    productContact: '',
    productContactLocation: '',
    productContactEmail: '',
    productContactPhone: '',
  });

  editForm = form(
    this.editModel,
    (schemaPath) => {
      requiredAndNotWhitespaceOnlySignal(schemaPath.brandName);
      requiredAndNotWhitespaceOnlySignal(schemaPath.legalEntityName);
      email(schemaPath.productContactEmail);
      pattern(schemaPath.productContactPhone, this.PHONE_NUMBER_PATTERN);
      maxLength(schemaPath.brandName, this.MAX_LENGTH);
      maxLength(schemaPath.legalEntityName, this.MAX_LENGTH);
      maxLength(schemaPath.productContact, this.MAX_LENGTH);
      maxLength(schemaPath.productContactLocation, this.MAX_LENGTH);
      maxLength(schemaPath.productContactEmail, this.MAX_LENGTH);
      maxLength(schemaPath.productContactPhone, this.MAX_LENGTH);
    },
    {
      submission: {
        action: async (field) => {
          await this.saveManufacturer(field().value());
        },
      },
    },
  );

  readonly editing = signal(false);
  readonly firstVisit = signal(false);

  constructor() {
    const manufacturer = this.appService.manufacturer();
    if (!manufacturer) {
      this.createNew();
    } else {
      this.manufacturer.set(manufacturer);
    }
  }

  protected edit() {
    const manufacturer = this.manufacturer();
    if (manufacturer) {
      // We cannot use the ApiManufacturer type here directly, because for some strange reason, the fields that are
      // optional and have no validator (productContact and productContactLocation) are considered invalid once they
      // are non-empty (Angular Bug?)
      this.editModel.set({
        brandName: manufacturer.brandName || '',
        legalEntityName: manufacturer.legalEntityName || '',
        productContact: manufacturer.productContact || '',
        productContactLocation: manufacturer.productContactLocation || '',
        productContactEmail: manufacturer.productContactEmail || '',
        productContactPhone: manufacturer.productContactPhone || '',
      });
      this.editing.set(true);
      setTimeout(() => {
        this.brandNameInput()?.nativeElement.focus();
      });
    }
  }

  protected createNew() {
    this.editing.set(true);
    this.firstVisit.set(true);
    setTimeout(() => {
      this.brandNameInput()?.nativeElement.focus();
    });
  }

  protected async saveManufacturer(manufacturer: ManufacturerProperties) {
    const updatedManufacturer = await firstValueFrom(
      this.apiService.saveManufacturer(manufacturer),
    );

    this.appService.manufacturer.set(updatedManufacturer);
    if (this.firstVisit()) {
      this.firstVisit.set(false);
      this.router.navigate(['dashboard']);
    } else {
      this.manufacturer.set(updatedManufacturer);
      this.editing.set(false);
    }
  }

  protected cancel() {
    this.editing.set(false);
  }
}
